import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { OrgRole, ProjectRole } from '@cosmic-safari/shared';
import { fetchMe, login as apiLogin, logout as apiLogout } from '@/api/endpoints/auth.js';
import { ApiError } from '@/api/client.js';

export interface AuthMembership {
  readonly projectId: string;
  readonly projectName: string;
  readonly role: ProjectRole;
}

export interface AuthUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly isStaff: boolean;
}

export interface AuthOrg {
  readonly id: string;
  readonly name: string;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const org = ref<AuthOrg | null>(null);
  // The caller's org role (SD-063 ticket 03); `null` for an external-only
  // caller with no org membership. Drives `canCreateProjects`.
  const orgRole = ref<OrgRole | null>(null);
  const memberships = ref<readonly AuthMembership[]>([]);
  const loading = ref(true);

  let bootstrapPromise: Promise<void> | null = null;

  const isAuthenticated = computed(() => user.value !== null);
  // Staff status lives on the `me` user; nulling `user` in clear() resets it,
  // so there is no separate staff state to keep in sync.
  const isStaff = computed(() => user.value?.isStaff ?? false);
  // Names the capability, not the role, so the affordance stays declarative and
  // a future role tweak changes one computed, not every call site (SD-063).
  const canCreateProjects = computed(
    () => orgRole.value === 'owner' || orgRole.value === 'admin'
  );
  // SD-064: archiving is org owner/admin, same signal as create today but named
  // as its own capability so the two gates stay independently steerable (a later
  // archive-only tweak changes this computed, not every call site) — the SD-063
  // "name the capability, not the role" rationale.
  const canArchiveProjects = computed(
    () => orgRole.value === 'owner' || orgRole.value === 'admin'
  );

  function roleFor(projectId: string): ProjectRole | null {
    const m = memberships.value.find((x) => x.projectId === projectId);
    return m?.role ?? null;
  }

  async function bootstrap(): Promise<void> {
    if (bootstrapPromise) return bootstrapPromise;
    loading.value = true;
    bootstrapPromise = (async () => {
      try {
        const res = await fetchMe();
        user.value = res.user;
        org.value = res.org ?? null;
        orgRole.value = res.orgRole ?? null;
        memberships.value = res.memberships;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          clear();
          return;
        }
        clear();
        throw err;
      } finally {
        loading.value = false;
      }
    })();
    return bootstrapPromise;
  }

  async function login(input: { email: string; password: string }): Promise<void> {
    await apiLogin(input);
    bootstrapPromise = null;
    await bootstrap();
  }

  // Force a fresh hydrate after the session cookie changes out-of-band (e.g. the
  // public bootstrap redeem, SD-041 ticket 15). Mirrors login()'s tail: drop the
  // cached bootstrap promise so bootstrap() re-fetches `me` instead of returning
  // the stale unauthenticated result.
  async function reload(): Promise<void> {
    bootstrapPromise = null;
    await bootstrap();
  }

  async function logout(): Promise<void> {
    try {
      await apiLogout();
    } finally {
      clear();
      bootstrapPromise = null;
    }
  }

  // Apply a user object the server just handed back (HUBL-072: the PATCH
  // /v1/auth/me response) so the caller's own session reflects a rename with no
  // `me` round trip. A whole-object replacement — AuthUser's fields are readonly
  // — and it touches nothing else on purpose: a rename changes no org, role or
  // membership, and dropping the cached bootstrap promise would quietly turn one
  // save into a full session refetch.
  function applyUser(next: AuthUser): void {
    user.value = next;
  }

  function clear(): void {
    user.value = null;
    org.value = null;
    orgRole.value = null;
    memberships.value = [];
  }

  return {
    user,
    org,
    orgRole,
    memberships,
    loading,
    isAuthenticated,
    isStaff,
    canCreateProjects,
    canArchiveProjects,
    roleFor,
    bootstrap,
    reload,
    login,
    logout,
    applyUser,
    clear,
  };
});
